import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../hooks/auth/useAuth';
import useLecutre from '../../../hooks/leacture-detail/useLecture';
import firestoreService from '../../../services/firestoreService';

const TIMES = [60, 90, 120];
const GOALS = [800, 1000, 1200];

const formatClock = (seconds) => {
  const safe = Math.max(0, seconds);
  const minutes = String(Math.floor(safe / 60)).padStart(2, '0');
  const rest = String(safe % 60).padStart(2, '0');
  return `${minutes}:${rest}`;
};

const Practice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lectureList } = useLecutre();
  const lectures = useMemo(() => (Array.isArray(lectureList) ? lectureList : []), [lectureList]);
  const [lectureId, setLectureId] = useState('');
  const [minutes, setMinutes] = useState(90);
  const [goal, setGoal] = useState(1200);
  const [draft, setDraft] = useState('');
  const [left, setLeft] = useState(90 * 60);
  const [running, setRunning] = useState(false);
  const [ended, setEnded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!lectureId && lectures[0]?.id) setLectureId(lectures[0].id);
  }, [lectureId, lectures]);

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => {
      setLeft((current) => {
        if (current <= 1) {
          setRunning(false);
          setEnded(true);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [running]);

  const start = () => {
    setLeft(minutes * 60);
    setEnded(false);
    setRunning(true);
    setMessage('');
  };

  const submit = async () => {
    if (!user?.uid || !lectureId) return;
    if (!draft.trim()) {
      setMessage('넘길 답안이 없습니다.');
      return;
    }
    setSaving(true);
    setRunning(false);
    const path = `LectureProgress/${lectureId}/LectureWriting`;
    const existing = await firestoreService.getDocument(path, user.uid);
    const result = existing.success
      ? await firestoreService.updateDocument(path, user.uid, { writing_content: draft })
      : await firestoreService.createDocument(path, { writing_content: draft }, user.uid);

    setSaving(false);
    if (!result.success) {
      setMessage(result.error || '답안을 저장하지 못했습니다.');
      return;
    }
    navigate(`/lecture/${lectureId}?step=3`);
  };

  return (
    <Page>
      <Kicker>실전</Kicker>
      <Title>시간을 재고 답안을 씁니다</Title>
      <Lead>끝나면 답안을 AI 첨삭 단계로 넘깁니다. 이미 있던 첨삭 기록은 지우지 않습니다.</Lead>

      <Setup>
        <label>
          문제
          <select value={lectureId} onChange={(event) => setLectureId(event.target.value)} disabled={running}>
            {lectures.map((lecture) => (
              <option key={lecture.id} value={lecture.id}>
                {lecture.name}
              </option>
            ))}
          </select>
        </label>
        <Choice>
          <span>제한 시간</span>
          <div>
            {TIMES.map((value) => (
              <Chip key={value} type="button" $on={minutes === value} disabled={running} onClick={() => setMinutes(value)}>
                {value}분
              </Chip>
            ))}
          </div>
        </Choice>
        <Choice>
          <span>목표 분량</span>
          <div>
            {GOALS.map((value) => (
              <Chip key={value} type="button" $on={goal === value} disabled={running} onClick={() => setGoal(value)}>
                {value.toLocaleString()}자
              </Chip>
            ))}
          </div>
        </Choice>
      </Setup>

      <Clock $ended={ended}>{formatClock(left)}</Clock>
      <Meta>
        {draft.length.toLocaleString()}자 / 목표 {goal.toLocaleString()}자
        {ended ? ' · 시간이 끝났습니다' : ''}
      </Meta>

      <Paper
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="여기에 실전 답안을 작성하세요."
      />

      <Actions>
        <Ghost type="button" onClick={start} disabled={running || !lectureId}>
          {left === minutes * 60 && !ended ? '시작' : '다시 시작'}
        </Ghost>
        <Solid type="button" onClick={submit} disabled={saving || !lectureId}>
          {saving ? '저장 중...' : 'AI 첨삭으로 넘기기'}
        </Solid>
      </Actions>
      {message ? <Message>{message}</Message> : null}
    </Page>
  );
};

export default Practice;

const Page = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #d9412b;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #1d2340;
`;

const Lead = styled.p`
  margin: 8px 0 20px;
  font-size: 15px;
  line-height: 1.6;
  color: #6f6a5c;
`;

const Setup = styled.div`
  display: grid;
  gap: 14px;
  margin-bottom: 18px;
  padding: 18px;
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 4px;

  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1d2340;
  }

  select {
    height: 42px;
    padding: 0 10px;
    font: inherit;
    border: 1px solid #e7e1d3;
    border-radius: 6px;
    background: #fff;
  }
`;

const Choice = styled.div`
  span {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1d2340;
  }

  div {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const Chip = styled.button`
  padding: 8px 12px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? '#1d2340' : '#6f6a5c')};
  background: ${({ $on }) => ($on ? '#ffe58a' : '#f8f5ee')};
  border: 1px solid #e7e1d3;
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const Clock = styled.p`
  margin: 0;
  font-size: 56px;
  font-weight: 700;
  letter-spacing: -0.04em;
  color: ${({ $ended }) => ($ended ? '#d9412b' : '#1d2340')};
`;

const Meta = styled.p`
  margin: 0 0 12px;
  color: #6f6a5c;
`;

const Paper = styled.textarea`
  width: 100%;
  min-height: 320px;
  padding: 18px;
  font: inherit;
  line-height: 1.75;
  color: #1d2340;
  background: #fffdf7;
  border: 1px solid #e7e1d3;
  border-radius: 4px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #1d2340;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Ghost = styled.button`
  padding: 13px 16px;
  font-weight: 700;
  color: #1d2340;
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
  }
`;

const Solid = styled.button`
  padding: 13px 16px;
  font-weight: 700;
  color: #fff;
  background: #1d2340;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
  }
`;

const Message = styled.p`
  margin: 10px 0 0;
  color: #d9412b;
`;
